import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { createFlowTestFlowTestMeasurement, deleteFlowTestMeasurement, getFlowTestFlowTestMeasurements, updateFlowTestMeasurement } from "../../api/flowTests/FlowTestMeasurement";
import { CreateFlowTestMeasurementRequest, UpdateFlowTestMeasurementRequest } from "../../types/model/flowTestMeasurement";

export interface FlowTestMeasurementFilterOptions {
  variableId?: number;
  startDate?: string;
  endDate?: string;
  minTestingTime?: number;
  maxTestingTime?: number;
  xAxis?: "measuredAt" | "testingTime";
}

export const useFlowTestMeasurements = (flowTestIdStr?: string) => {
  const queryClient = useQueryClient();
  const flowTestId = Number(flowTestIdStr);

  const [page, setPage] = useState(0);
  const [filtersState, setFiltersState] = useState<FlowTestMeasurementFilterOptions>({
    xAxis: "measuredAt",
  });

  const setFilters = (newFilters: FlowTestMeasurementFilterOptions) => {
    setFiltersState(newFilters);
    setPage(0);
  };

  const queryKey = ["flow-test", flowTestId, "measurements", {
    variableId: filtersState.variableId,
    startDate: filtersState.startDate,
    endDate: filtersState.endDate,
    minTestingTime: filtersState.minTestingTime,
    maxTestingTime: filtersState.maxTestingTime,
    sortBy: filtersState.xAxis,
  }];

  const result = useQuery({
    queryKey,
    queryFn: () => getFlowTestFlowTestMeasurements(
      flowTestId, 
      filtersState.variableId, 
      filtersState.startDate, 
      filtersState.endDate,
      filtersState.minTestingTime,
      filtersState.maxTestingTime,
      filtersState.xAxis
    ),
    enabled: !!flowTestId,
    placeholderData: keepPreviousData,
  });

  const createMutation = useMutation({
    mutationFn: (values: CreateFlowTestMeasurementRequest) => createFlowTestFlowTestMeasurement(flowTestId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flow-test", flowTestId, "measurements"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ measurementId, values }: { measurementId: number; values: UpdateFlowTestMeasurementRequest }) => updateFlowTestMeasurement(measurementId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flow-test", flowTestId, "measurements"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (measurementId: number) => deleteFlowTestMeasurement(measurementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flow-test", flowTestId, "measurements"] });
    },
  });

  return {
    measurementsData: result.data ?? [],
    isPending: result.isPending,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    filters: filtersState,
    setFilters,
    page,
    setPage,
    refetch: result.refetch,
    createMeasurement: createMutation.mutateAsync,
    updateMeasurement: updateMutation.mutateAsync,
    deleteMeasurement: deleteMutation.mutateAsync,
    isUpdating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};
